import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { DataGrid } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExpertReportFeedbackItem } from '@/entities/expert-report';
import type { DataGridColumnStateProps } from '@/entities/settings';
import { useExpertReportFeedbackColumns } from './useExpertReportFeedbackColumns';

type Props = {
  totalCount: number;
  pageSize: number;
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<ExpertReportFeedbackItem[]>;
  /** Подпись состава запроса: её смена сбрасывает загруженные порции */
  cacheVersion: string;
  sorting: DataGridSortingState;
  filters: DataGridFilterState;
  columnState: DataGridColumnStateProps;
  onSortingChange: (sorting: DataGridSortingState) => void;
  onFiltersChange: (filters: DataGridFilterState) => void;
};

/**
 * Презентер раздела «Обратная связь»: заголовок и грид правок с настраиваемыми колонками
 * и догрузкой порций по мере прокрутки.
 */
export const ExpertReportFeedbackTable: FC<Props> = ({
  totalCount,
  pageSize,
  fetchPage,
  cacheVersion,
  sorting,
  filters,
  columnState,
  onSortingChange,
  onFiltersChange,
}) => {
  const { t } = useTranslation(['expertReport']);
  const columns = useExpertReportFeedbackColumns();

  return (
    <>
      <h1 className="text-2xl font-semibold">{t('expertReport:feedback.title')}</h1>
      <div className="flex-1 min-h-0" data-testid="expert-report-feedback-table">
        <DataGrid
          {...columnState}
          className="h-full w-full"
          columns={columns}
          dataSource={{
            mode: 'lazy',
            totalCount,
            pageSize,
            fetchPage,
            cacheVersion,
          }}
          getRowId={(row) => `${row.conversationId}-${row.expertReportId}-${row.blockId}-${row.version}`}
          sorting={sorting}
          onSortingChange={onSortingChange}
          filters={filters}
          onFiltersChange={onFiltersChange}
          enableColumnResizing
          enableColumnReordering
          enableColumnPinning
          enableColumnHiding
          columnsPanel
          wrapText
          emptyState={<span className="text-muted-foreground">{t('expertReport:feedback.empty')}</span>}
        />
      </div>
    </>
  );
};
